import type { MenuBoardContent } from '../types.js';
import type { MenuTemplate } from './common.js';
import {
  FC_THEME,
  FC_VARIANTS,
  fcBlocks,
  fcColumn,
  fcCss,
  fcEmpty,
  fcPaginate,
  fcPhoto,
  fcPhotoCount,
  fcPhotoIds,
  fcTitle,
  fcTitleLayout,
  type FcTitleSpec,
} from './food-collage-mosaic.js';

/**
 * Food collage, photo strip: the title and a row of dish photos across the top of the
 * chalkboard, and three labelled lists underneath.
 */

const S = '.mb-t-food-collage-strip';
const P = 'fcs';

const TITLE_LANDSCAPE: FcTitleSpec = { width: 30, script: 3, caps: 4.6, maxLines: 2 };
const TITLE_PORTRAIT: FcTitleSpec = { width: 60, script: 3.4, caps: 6.2, maxLines: 2 };
/** The strip is at least this tall; a taller title stretches it and the photos with it. */
const BAND = 22;
const PORTRAIT_PHOTO = 25;

function geometry(board: MenuBoardContent, portrait: boolean) {
  const photos = fcPhotoCount(board, portrait ? 3 : 4);
  if (portrait) {
    const title = fcTitleLayout(board, TITLE_PORTRAIT);
    const capacity = 116 - title.height - 1.6 - (photos ? PORTRAIT_PHOTO + 1.8 : 0);
    return { photos, title, band: 0, columns: [fcColumn(board, capacity, 30), fcColumn(board, capacity, 30)] };
  }
  const title = fcTitleLayout(board, TITLE_LANDSCAPE);
  const band = Math.max(BAND, title.height);
  const capacity = 63.5 - band - 2;
  return { photos, title, band, columns: [0, 1, 2].map(() => fcColumn(board, capacity, 36)) };
}

export const foodCollageStrip: MenuTemplate = {
  id: 'food-collage-strip',
  name: 'Food collage · Photo strip',
  description:
    'The chalkboard collage look with a strip of dish photos beside the title across the top, and three labelled lists below.',
  defaultTheme: FC_THEME,
  variants: FC_VARIANTS,
  cuisines: ['burgers', 'pizza', 'pasta', 'cafe'],
  family: { id: 'food-collage', name: 'Food Collage', layout: 'Photo strip' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return fcPaginate(board, geometry(board, orientation === 'portrait').columns);
  },

  render(ctx) {
    const portrait = ctx.orientation === 'portrait';
    const { photos, title, band } = geometry(ctx.board, portrait);
    const tiles = fcPhotoIds(ctx, photos).map((id) => fcPhoto(ctx, id, P)).join('');
    const strip = tiles ? `<div class="fcs-photos">${tiles}</div>` : '';
    const cols = ctx.page.columns
      .map((c, i) => `<div class="fcs-list">${fcBlocks(ctx, c, P, portrait ? 30 : 36)}${i === 0 ? fcEmpty(ctx) : ''}</div>`)
      .join('');
    const top = portrait
      ? `${fcTitle(ctx, P, title)}${strip}`
      : `<div class="fcs-band${tiles ? '' : ' fcs-solo'}" style="height:${band.toFixed(2)}em">${fcTitle(ctx, P, title)}${strip}</div>`;
    return `${top}<div class="fcs-cols">${cols}</div>`;
  },

  css:
    fcCss(S, P) +
    `
${S} .fcs-band{flex:0 0 auto;display:grid;grid-template-columns:30em minmax(0,1fr);grid-template-rows:minmax(0,1fr);gap:2.4em;align-items:stretch}
${S} .fcs-band.fcs-solo{grid-template-columns:1fr}
${S} .fcs-photos{display:flex;gap:1.4em;min-width:0;min-height:0}
${S} .fcs-photos .fcs-ph{flex:1 1 0;height:100%}
${S}.mb-portrait .fcs-photos{flex:0 0 ${PORTRAIT_PHOTO}em;margin-top:1.6em}
${S} .fcs-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0 2.6em;margin-top:2em}
${S}.mb-portrait .fcs-cols{grid-template-columns:repeat(2,minmax(0,1fr));gap:0 2.4em;margin-top:1.8em}
${S} .fcs-cols .fcs-label{min-width:55%}
`,
};
